/**
 * docSuggest.js — filename-based label suggestions + next-number generators.
 *
 * The team's file naming is disciplined (every quote is "GAT-B-####"), so the
 * upload form can usually label a file by itself: parse the name, then cross-
 * check any number in it against the project's own quote/PO/invoice numbers.
 * The user always sees the suggestion prefilled and can override it.
 *
 * Numbers: quote letters are one company-wide series (…5336, 5337) and invoice
 * numbers one zero-padded series (…00512), so "next" is max + 1. PO numbers
 * are issued BY the customer — no pattern of ours — so no generator for them.
 */
import { normalizeDocRef } from './attachmentPreview';

/** Suggest {type, ref, why} from a filename, or null. `ctx` carries the
 *  project's own numbers: { quote, po, invoices: [] }. */
export function suggestLabel(filename, ctx = {}) {
  const f = String(filename || '');
  const gat = f.match(/GAT-B-?\s*(\d{3,5})/i);
  if (gat) return { type: 'quote', ref: gat[1], why: 'quote number in the file name' };
  const inv = f.match(/\binv(?:oice)?[\s_#-]*(\d{3,})/i);
  if (inv) return { type: 'invoice', ref: inv[1], why: 'invoice number in the file name' };
  const po = f.match(/\bP\.?O\.?[\s_#-]*([A-Za-z]{0,3}\d[\w-]{2,})/i);
  if (po) return { type: 'po', ref: po[1], why: 'PO number in the file name' };
  // No keyword — try every number in the name against the project's numbers.
  for (const n of f.match(/\d{3,}/g) || []) {
    const nn = normalizeDocRef(n);
    if (ctx.po && normalizeDocRef(ctx.po) === nn) return { type: 'po', ref: ctx.po, why: "matches this project's PO number" };
    const hit = (ctx.invoices || []).find((x) => normalizeDocRef(x) === nn);
    if (hit) return { type: 'invoice', ref: hit, why: 'matches an invoice number on this project' };
    if (ctx.quote && normalizeDocRef(ctx.quote) === nn)
      return { type: 'quote', ref: ctx.quote, why: "matches this project's quote number" };
  }
  return null;
}

/**
 * suggestLabel, but only when the number is THIS record's own. For a multi-file
 * drop, which labels without asking: a "GAT-B-5300" cost grid dropped on job
 * 5356 was being labelled Quote 5300 - or, worse, Quote 5356 for a cost-grid
 * .xlsm that the Quote # link could then open. No confident match = General.
 */
export function confirmedLabel(filename, ctx = {}) {
  const s = suggestLabel(filename, ctx);
  if (!s) return null;
  const n = normalizeDocRef(s.ref);
  if (s.type === 'quote' && ctx.quote && normalizeDocRef(ctx.quote) === n) return { ...s, ref: ctx.quote };
  if (s.type === 'po' && ctx.po && normalizeDocRef(ctx.po) === n) return { ...s, ref: ctx.po };
  if (s.type === 'invoice') {
    const hit = (ctx.invoices || []).find((x) => normalizeDocRef(x) === n);
    if (hit) return { ...s, ref: hit };
  }
  return null;
}

/** Split an invoice-numbers cell ("00493, 00512") into its numbers. */
export const splitInvoiceNumbers = (raw) =>
  String(raw || '')
    .split(/[,;/]+/)
    .map((s) => s.trim())
    .filter(Boolean);

const numericMax = (values) =>
  values.reduce((max, v) => {
    const n = parseInt(String(v).replace(/\D/g, ''), 10);
    return Number.isFinite(n) && n > max ? n : max;
  }, 0);

/** Next free quote number across every project ("5338"). */
export function nextQuoteNumber(projects) {
  const letters = (projects || []).map((p) => p.quoteLetter ?? p.quote_letter).filter((q) => /^\d{3,5}$/.test(String(q ?? '').trim()));
  const max = numericMax(letters);
  return max ? String(max + 1) : '';
}

/** Next invoice number across every project, zero-padded like the series
 *  ("00513" after "00512"). */
export function nextInvoiceNumber(projects) {
  let max = 0,
    width = 5;
  for (const p of projects || []) {
    for (const n of splitInvoiceNumbers(p.invoiceNumbers ?? p.invoice_numbers)) {
      if (!/^\d{3,7}$/.test(n)) continue; // skip customer-format refs
      const v = parseInt(n, 10);
      if (v > max) {
        max = v;
        width = n.length;
      }
    }
  }
  return max ? String(max + 1).padStart(width, '0') : '';
}
